<template>
  <UIAlert v-if="sugarDown">
    {{ sugarDownText }}
  </UIAlert>

  <TrialFormComplete
    v-else-if="response"
    :automated-trial-text="automatedTrialText"
    :default-product="productId"
    :automatically-setup-trials="response?.automatically_setup_trials"
    :existing-licence-error="response?.existing_licence_error"
    :trial-request-unavailable="response?.trialRequestUnavailable"
    :no-automated-trial-text="noAutomatedTrialText"
    :products="products"
  />

  <FormWizard
    v-else
    recaptcha
    :error="!!error"
    :loading="loading"
    @submit="onSubmit"
  >
    <HiddenField name="form-type" :modelValue="formType" />
    <HiddenField name="csrfmiddlewaretoken" :modelValue="csrf" />
    <HiddenField :name="`${formType}-trial_option`" modelValue="none" />
    <HiddenField
      :name="`${formType}-form_source`"
      :modelValue="cookieFormSource"
    />
    <HiddenField
      :name="`${formType}-extended_trial`"
      :modelValue="extendedTrial"
    />
    <HiddenField
      :name="`${formType}-product_license`"
      :modelValue="productId"
    />
    <HiddenField :name="`${formType}-${productId}`" modelValue="on" />
    <HiddenField name="role-init" :modelValue="role" />
    <HiddenField :name="`${formType}-org_id`" :modelValue="org_id" />
    <HiddenField :name="`${formType}-campaign_id`" :modelValue="campaignId" />
    <HiddenField :name="`${formType}-subtype`" :modelValue="subtype" />

    <h3 class="text-center text-primary">
      {{ title }}
    </h3>

    <UIAlert v-if="buyItNow && role" class="text-center" variant="dark">
      <strong>{{ friendlyRoles[role] }}</strong>
    </UIAlert>

    <UIAlert v-if="buyItNow" variant="warning">
      {{ buyItNowText }}
    </UIAlert>

    <FormStep age-field terms-field>
      <slot name="school-fields"></slot>
    </FormStep>

    <template #submit="submitProps">
      <SubmitButton v-bind="submitProps">
        {{ buyItNow ? buyItNowButtonText : buttonText }}
      </SubmitButton>
    </template>
  </FormWizard>
</template>

<script lang="ts">
type TrialFormServerResponseData = {
  automatically_setup_trials: string[] | undefined;
  existing_licence_error: string | undefined;
  trialRequestUnavailable: boolean | undefined;
};

export type ProductTrialFormContext = {
  excludeRoles: string[];
};

const productTrialFormContextKey = "product-trial-form";

export const useProductTrialFormContext = () => {
  return inject<ProductTrialFormContext>(productTrialFormContextKey);
};
</script>

<script setup lang="ts">
import { trialFormSettings } from "@/app/settings";
import UIAlert from "@/components/UI/UIAlert.vue";
import { useDialog } from "@/components/UI/dialog/useDialog";
import HiddenField from "@/components/fields/HiddenField.vue";
import { sendFormData } from "@/lib/submitForm";
import { useCookies } from "@vueuse/integrations/useCookies";
import { GenericObject } from "vee-validate";
import { computed, inject, provide, reactive, ref } from "vue";
import FormStep from "../FormStep.vue";
import FormWizard from "../FormWizard.vue";
import SubmitButton from "../SubmitButton.vue";
import { RoleType, useContactCaptureProvider } from "../useContactCapture";
import { ProductTrialDialogId } from "./ProductTrialDialog.vue";
import TrialFormComplete from "./TrialFormComplete.vue";

type Props = {
  csrf: string;
  automatedTrialText: string;
  /** Text to display for products with manual trial activation. */
  noAutomatedTrialText: string;
  /** Label for the trial signup button. */
  buttonText: string;
  /** Text for the "buy" section. */
  buyItNowText: string;
  /** Label for the "buy now" button. */
  buyItNowButtonText: string;
  /** Product ID */
  productId: string;
  /** Whether the service is unavailable. */
  sugarDown: boolean;
  /** Error message for service unavailable. */
  sugarDownText: string;
  /** The initial value for the `role` input. */
  initialRole: RoleType;
  /** Form title. */
  title: string;
  campaignId: string;
  extendedTrial: string;
  subtype: string;
};

const formType = "trial-signup";

const friendlyRoles: Partial<Record<RoleType, string>> = {
  School: "Mini Mash for nurseries costs £240/year including VAT",
  Home_user: "Mini Mash for parents costs £30/year including VAT",
  Tutor: "Mini Mash for parents costs £30/year including VAT",
  Other: " Mini Mash costs £240/year including VAT",
};
const combinedBundles: Record<string, string[]> = {
  minimash_evidenceme_bundle: ["minimash", "buildaprofile"],
};

const props = defineProps<Props>();
const cookies = useCookies(["url_source"]);

const { role, org_id } = useContactCaptureProvider("trial-signup-", {
  role: props.initialRole,
});

const products = reactive<string[]>(
  combinedBundles[props.productId]
    ? combinedBundles[props.productId]
    : props.productId
      ? [props.productId]
      : []
);

const response = ref<TrialFormServerResponseData>();
const error = ref<string | null>(null);
const loading = ref<boolean>(false);

const { options } = useDialog(ProductTrialDialogId);
const buyItNow = computed(() => !!options.value?.["buyItNow"]);

const cookieFormSource =
  cookies.get("url_source") ?? trialFormSettings.formSource;

/**
 * Form `submit` event handler.
 */
const onSubmit = async (values: GenericObject) => {
  // For some reason the server is expecting school_size even if not visible.
  const data: Record<string, unknown> = {
    ...values,
    "trial-signup-school_size": "",
  };

  let result: {
    success: boolean;
    data: TrialFormServerResponseData;
  };

  console.log("loading...");
  loading.value = true;

  try {
    console.log("request sent...");
    result = await sendFormData("/forms/", data);
  } catch (e) {
    console.error(e);
    error.value =
      "The service is currently unavailable. Please try again later.";
    return;
  } finally {
    console.log("Done!");
    loading.value = false;
  }

  response.value = result.data;
};

provide<ProductTrialFormContext>(productTrialFormContextKey, {
  excludeRoles:
    trialFormSettings.productLicenceChoices.find(
      (p) => p.field_name === props.productId
    )?.exclude_roles ?? [],
});
</script>
